import "./detail.css";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Button, Form } from "react-bootstrap";

import { handleError } from "../../helpers/handleError";
import { getOrganizerOrderLabel } from "../../helpers/orderNumber";
import {
	getMyEventDetailApi,
	initiateTicketTransferApi,
	getAppSettings,
} from "../../api/api";
import RollingQr from "../../components/qr/RollingQr";
import AuthorizedQrImage from "../../components/qr/AuthorizedQrImage";
import { MAIN_API } from "../../api/apilist";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Calendar from "../../assets/image/calendario 1.svg";
import ubi from "../../assets/image/ubicacion 1.svg";
import ticketIco from "../../assets/image/boleto-2 1.svg";
import qrIcon from "../../assets/image/codigo-qr 1.svg";
import transferIcon from "../../assets/image/transferencia-de-datos 1.svg";
import calendario from "../../assets/image/calendario-3 1.svg";
import shareIcon from "../../assets/image/arrow-up-from-bracket-solid-full (1).svg";
import instagramIcon from "../../assets/image/instagram-brands-solid-full.svg";

const escapeHtml = (value) =>
	String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

const idFrom = (v) => {
	if (!v) return "";
	if (typeof v === "object") return String(v._id || v.id || v.userid || "");
	return String(v);
};

// Single source of truth for a ticket's transfer state: used both to order the
// list and to render each card, so both always agree.
const resolveTicketTransfer = (t, currentUserId) => {
	const statusStrs = [t?.status, t?.state, t?.transferStatus].map((v) =>
		String(v || "").toLowerCase(),
	);
	const transferStatusRaw = String(t?.transferStatus || "").toLowerCase();
	const curId = String(currentUserId || "");
	const ownerCandidates = [
		t?.ownerId,
		t?.ownerid,
		t?.userId,
		t?.userid,
		t?.owner,
		t?.user,
		t?.beneficiary,
		t?.toUser,
		t?.receiver,
		t?.recipient,
	];
	const ownerIds = ownerCandidates.map(idFrom).filter(Boolean);
	const knownOwnerDifferent =
		ownerIds.length > 0 && curId && ownerIds.every((id) => id !== curId);
	const legacyPending =
		t?.pendingTransfer === true ||
		t?.transferPending === true ||
		statusStrs.some((s) => s.includes("pending"));
	const legacyTransferred =
		t?.transferred === true ||
		t?.isTransferred === true ||
		t?.transfered === true /* typo variant */ ||
		t?.transfer === true ||
		statusStrs.some(
			(s) => s.includes("transferred") || s.includes("transfer"),
		) ||
		knownOwnerDifferent;
	const fallbackStatus = legacyTransferred
		? "transferred"
		: legacyPending
			? "pending"
			: "";
	const resolvedStatus = transferStatusRaw || fallbackStatus || "owned";
	return {
		resolvedStatus,
		isTransferred: resolvedStatus === "transferred",
		isTransferPending:
			resolvedStatus === "pending" || resolvedStatus === "incoming",
	};
};

// Display order: transfers awaiting acceptance first, then usable tickets,
// then already scanned ones, and finally the tickets given away.
const TICKET_GROUP = {
	PENDING: 0,
	UNUSED: 1,
	USED: 2,
	TRANSFERRED: 3,
};

const ticketGroup = (t, currentUserId) => {
	const { isTransferred, isTransferPending } = resolveTicketTransfer(
		t,
		currentUserId,
	);
	if (isTransferPending) return TICKET_GROUP.PENDING;
	if (isTransferred) return TICKET_GROUP.TRANSFERRED;
	if (String(t?.status || "").toLowerCase() === "redeemed")
		return TICKET_GROUP.USED;
	return TICKET_GROUP.UNUSED;
};

// The API does not expose a per-ticket purchase date, only the order's date via
// `detail.purchases`. Map orderId -> purchase timestamp so tickets can be sorted
// by when they were actually bought.
const buildPurchaseDates = (purchases) => {
	const dates = new Map();
	(Array.isArray(purchases) ? purchases : []).forEach((p) => {
		const parsed = p?.createdAt ? dayjs(p.createdAt) : null;
		if (p?.id && parsed && parsed.isValid())
			dates.set(String(p.id), parsed.valueOf());
	});
	return dates;
};

// Recency key, newest first. `seq` only breaks ties inside a single order: it
// restarts at 1 for every purchase, so it is not comparable across orders.
const ticketRecency = (t, purchaseDates) => {
	const purchasedAt = purchaseDates.get(String(t?.orderId ?? ""));
	const seq = Number(t?.seq);
	return {
		purchasedAt: Number.isFinite(purchasedAt) ? purchasedAt : null,
		seq: Number.isFinite(seq) ? seq : 0,
	};
};

const sortTicketsForDisplay = (tickets, currentUserId, purchases) => {
	const purchaseDates = buildPurchaseDates(purchases);
	return [...tickets]
		.map((t, index) => ({ t, index }))
		.sort((a, b) => {
			const groupDiff =
				ticketGroup(a.t, currentUserId) - ticketGroup(b.t, currentUserId);
			if (groupDiff !== 0) return groupDiff;

			const ra = ticketRecency(a.t, purchaseDates);
			const rb = ticketRecency(b.t, purchaseDates);
			if (ra.purchasedAt !== null && rb.purchasedAt !== null) {
				const dateDiff = rb.purchasedAt - ra.purchasedAt;
				if (dateDiff !== 0) return dateDiff;
			} else if (ra.purchasedAt !== rb.purchasedAt) {
				return ra.purchasedAt === null ? 1 : -1; // dated tickets first
			}

			const seqDiff = rb.seq - ra.seq;
			if (seqDiff !== 0) return seqDiff;
			return a.index - b.index; // stable for ties
		})
		.map(({ t }) => t);
};

const MyEventDetail = () => {
	const { eventid } = useParams();
	const token = useSelector((s) => s.auth.accessToken);
	const currentUserId = useSelector(
		(s) => s.auth?.user?._id || s.auth?.user?.id || s.auth?.user?.userid,
	);
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(false);
	const [showTransfer, setShowTransfer] = useState(false);
	const [showTicket, setShowTicket] = useState(false);
	const [ticketView, setTicketView] = useState(null);
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [toEmail, setToEmail] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const navigate = useNavigate();
	const gridRef = useRef(null);
	const [isMobile, setIsMobile] = useState(false);
	const [appSettings, setAppSettings] = useState({});
	const mountedRef = useRef(false);
	const currentRequestKeyRef = useRef("");
	const inFlightRequestsRef = useRef(new Map());
	const requestIdRef = useRef(0);
	const buildRequestKey = (nextToken, nextEventId) =>
		`${nextToken || ""}|${nextEventId || ""}`;
	currentRequestKeyRef.current = buildRequestKey(token, eventid);
	const handleShare = async () => {
		try {
			const shareData = {
				title: detail?.name || "Mi evento",
				text: "Mirá este evento en Zoco",
				url:
					typeof window !== "undefined" && window.location
						? window.location.href
						: "",
			};
			if (navigator && typeof navigator.share === "function") {
				await navigator.share(shareData);
			} else if (
				navigator &&
				navigator.clipboard &&
				typeof navigator.clipboard.writeText === "function"
			) {
				await navigator.clipboard.writeText(shareData.url);
				window &&
					window.alert &&
					window.alert("Enlace copiado al portapapeles");
			}
		} catch (_) {
			try {
				const url =
					typeof window !== "undefined" && window.location
						? window.location.href
						: "";
				if (
					navigator &&
					navigator.clipboard &&
					typeof navigator.clipboard.writeText === "function"
				) {
					await navigator.clipboard.writeText(url);
					window &&
						window.alert &&
						window.alert("Enlace copiado al portapapeles");
				}
			} catch {}
		}
	};

	const nav = [
		{ title: "Inicio", path: "/event" },
		{ title: "Mis eventos", path: "/myevents" },
		{ title: "Detalle", path: `/myevents/${eventid}` },
	];

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	// `background` refreshes keep the current content on screen: no spinner and no
	// transient error state, so a scan detected by polling only swaps ticket data.
	const fetchDetail = async (background = false) => {
		if (!mountedRef.current || !token || !eventid) return;
		const requestKey = buildRequestKey(token, eventid);
		if (inFlightRequestsRef.current.has(requestKey)) return;
		const requestId = ++requestIdRef.current;
		inFlightRequestsRef.current.set(requestKey, requestId);
		try {
			if (!background) setLoading(true);
			const res = await getMyEventDetailApi(token, eventid);
			if (
				mountedRef.current &&
				currentRequestKeyRef.current === requestKey &&
				res.status === 200
			) {
				setDetail(res.data.info);
			}
		} catch (error) {
			if (
				mountedRef.current &&
				currentRequestKeyRef.current === requestKey &&
				!background
			)
				handleError(error);
		} finally {
			if (inFlightRequestsRef.current.get(requestKey) === requestId) {
				inFlightRequestsRef.current.delete(requestKey);
				if (
					mountedRef.current &&
					currentRequestKeyRef.current === requestKey &&
					!background
				)
					setLoading(false);
			}
		}
	};

	useEffect(() => {
		if (token && eventid) fetchDetail();
	}, [token, eventid]);

	useEffect(() => {
		if (!token || !eventid) return;
		const refreshIfVisible = () => {
			if (document.visibilityState === "visible") fetchDetail(true);
		};
		document.addEventListener("visibilitychange", refreshIfVisible);
		window.addEventListener("focus", refreshIfVisible);
		const interval = window.setInterval(refreshIfVisible, 5000);
		return () => {
			document.removeEventListener("visibilitychange", refreshIfVisible);
			window.removeEventListener("focus", refreshIfVisible);
			window.clearInterval(interval);
		};
	}, [token, eventid]);

	useEffect(() => {
		(async () => {
			try {
				const res = await getAppSettings();
				if (mountedRef.current && res?.status === 200)
					setAppSettings(res.data.info || {});
			} catch (_) {}
		})();
	}, []);

	// Detect mobile to enable the scaling carousel effect
	useEffect(() => {
		try {
			const mql = window.matchMedia("(max-width: 640px)");
			const sync = () => setIsMobile(!!mql.matches);
			sync();
			if (mql.addEventListener) mql.addEventListener("change", sync);
			else if (mql.addListener) mql.addListener(sync);
			return () => {
				if (mql.removeEventListener) mql.removeEventListener("change", sync);
				else if (mql.removeListener) mql.removeListener(sync);
			};
		} catch (_) {}
	}, []);

	// Center-scaling effect: the card nearest to the horizontal center is full size
	useEffect(() => {
		if (!isMobile) return;
		const grid = gridRef.current;
		if (!grid) return;
		const items = () => Array.from(grid.querySelectorAll(".ticket-item"));
		const update = () => {
			try {
				const c = grid.scrollLeft + grid.clientWidth / 2;
				let best = null;
				let bestD = Infinity;
				items().forEach((el) => {
					const mid = el.offsetLeft + el.offsetWidth / 2;
					const d = Math.abs(mid - c);
					if (d < bestD) {
						bestD = d;
						best = el;
					}
				});
				items().forEach((el) => el.classList.toggle("is-center", el === best));
			} catch (_) {}
		};
		const onScroll = () => window.requestAnimationFrame(update);
		update();
		grid.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll);
		// Run once after images load to ensure sizes
		const t = setTimeout(update, 50);
		return () => {
			clearTimeout(t);
			grid.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
		};
	}, [isMobile, detail?.tickets?.length]);

	// Loading consistente con el resto de páginas
	if (loading) return <SpinLoading />;

	if (!detail) {
		return (
			<>
				<div className="myevent-detail pading150">No se encontraron datos.</div>
			</>
		);
	}

	const orderedTickets = Array.isArray(detail.tickets)
		? sortTicketsForDisplay(detail.tickets, currentUserId, detail.purchases)
		: [];
	const ticketsCount = Number(
		detail.ticketsPurchased ||
			(Array.isArray(detail.tickets) ? detail.tickets.length : 0),
	);
	const dStart = dayjs(String(detail.startDate || "").slice(0, 10));
	const dateLabel = dStart.isValid() ? dStart.format("D MMM. - YYYY") : "";
	const timeLabel = String(detail.timeFrom || "").slice(0, 5);
	// The modal must follow polling, not the snapshot taken when it was opened:
	// re-read the ticket from the freshest `detail` so a scan detected in the
	// background removes the QR while the modal stays open.
	const activeTicket =
		(ticketView?.ticket &&
			Array.isArray(detail.tickets) &&
			detail.tickets.find(
				(t) => String(t.id) === String(ticketView.ticket.id),
			)) ||
		ticketView?.ticket;
	const ticketMeta = ticketView?.meta || {};
	const liveTransfer = activeTicket
		? resolveTicketTransfer(activeTicket, currentUserId)
		: null;
	const metaTransferred = liveTransfer?.isTransferred === true;
	const metaPending = liveTransfer?.isTransferPending === true;
	const isRedeemedTicket =
		String(activeTicket?.status || "").toLowerCase() === "redeemed";
	const activeQrPolicy = activeTicket?.qrPolicy || {
		version: 1,
		mode: "dynamic",
		dynamicRequired: true,
		staticAllowed: false,
	};
	const isDynamicTicket = !!(
		activeTicket &&
		activeQrPolicy.dynamicRequired &&
		!metaTransferred &&
		!metaPending &&
		!isRedeemedTicket
	);
	const staticQrUrl =
		activeTicket && activeQrPolicy.staticAllowed && !isDynamicTicket && !metaTransferred && !isRedeemedTicket
			? `${MAIN_API}/api/ticket/qr/ticket/${activeTicket.id}.png`
			: "";
	const ticketStateLabel = activeTicket
		? activeTicket.status === "redeemed"
			? "Usada"
			: metaTransferred
				? "Transferida"
				: metaPending
					? "Pendiente"
					: "Vigente"
		: "";
	const stateValueClass =
		ticketStateLabel === "Usada" || metaTransferred
			? "is-used"
			: metaPending
				? "is-pending"
				: "is-active";
	const transferValueClass = metaTransferred
		? "is-used"
		: metaPending
			? "is-pending"
			: "is-active";

	const openTransfer = (ticket) => {
		setSelectedTicket(ticket);
		setToEmail("");
		setShowTransfer(true);
	};

	const openTicket = (ticket, meta) => {
		setTicketView({ ticket, meta });
		setShowTicket(true);
	};

	const closeTicket = () => {
		setShowTicket(false);
		setTicketView(null);
	};

	const handlePrintTicket = async (ticket, meta = {}) => {
		if (!ticket?.qrPolicy?.staticAllowed) {
			toast.info("Esta entrada requiere mostrar el QR dinámico en vivo.");
			return;
		}
		const popup = window.open("", "_blank", "width=900,height=900");
		if (!popup) {
			toast.error(
				"No pudimos abrir la vista para imprimir. Permiti las ventanas emergentes.",
			);
			return;
		}
		const writePlaceholder = (message) => {
			popup.document.open();
			popup.document.write(
				`<!DOCTYPE html><html><body style="font-family: Arial, sans-serif; padding: 40px;">${message}</body></html>`,
			);
			popup.document.close();
		};
		writePlaceholder("Generando QR estatico...");
		try {
			const staticQrUrl = `${MAIN_API}/api/ticket/qr/ticket/${ticket.id}/static?format=png&ts=${Date.now()}`;
			const qrResponse = await fetch(staticQrUrl, {
				headers: { Authorization: `Bearer ${token}` },
				cache: "no-store",
			});
			if (!qrResponse.ok) throw new Error(`HTTP ${qrResponse.status}`);
			const qrObjectUrl = URL.createObjectURL(await qrResponse.blob());
			const seq = escapeHtml(ticket?.seq ? `Entrada ${ticket.seq}` : "Entrada");
			const eventName = escapeHtml(detail?.name || "Mi evento");
			const location = escapeHtml(detail?.fulladdress || "");
			const dateText = escapeHtml(
				meta.fullDate || dateLabel || meta.datePretty || "",
			);
			const timeText = escapeHtml(
				meta.fullTime ||
					(timeLabel ? `${timeLabel} hs` : meta.timePretty || ""),
			);
			const orderLabel = escapeHtml(meta.orderLabel || "-");
			const statusLabel = escapeHtml(meta.ticketStateLabel || "Vigente");
			const heroImg = detail?.image
				? `<img src="${escapeHtml(detail.image)}" alt="Evento" class="ticket-sheet__hero" />`
				: "";
			const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>${seq} - ${eventName}</title>
  <style>
    * { box-sizing: border-box; font-family: "Inter", Arial, sans-serif; }
    body { margin: 0; padding: 32px; background: #f3f4f6; color: #111827; }
    main { max-width: 720px; margin: 0 auto; background: #fff; border-radius: 24px; padding: 32px; box-shadow: 0 20px 60px rgba(15,23,42,0.15); }
    .ticket-sheet__hero { width: 100%; border-radius: 18px; margin-bottom: 24px; object-fit: cover; max-height: 320px; }
    .ticket-sheet__header { display: flex; flex-direction: column; gap: 4px; margin-bottom: 20px; }
    .ticket-sheet__title { font-size: 28px; font-weight: 700; margin: 0; }
    .ticket-sheet__meta { font-size: 16px; color: #4b5563; }
    .ticket-sheet__grid { display: flex; gap: 24px; flex-wrap: wrap; align-items: flex-start; }
    .ticket-sheet__qr-wrapper { display: flex; flex-direction: column; align-items: flex-start; gap: 10px; }
    .ticket-sheet__qr-note { font-size: 13px; color: #4b5563; line-height: 1.3; }
    .ticket-sheet__qr { width: 280px; height: 280px; object-fit: contain; border-radius: 16px; border: 1px solid #e5e7eb; padding: 12px; background: #f9fafb; }
    .ticket-sheet__info { flex: 1; min-width: 220px; display: grid; gap: 14px; }
    .ticket-sheet__info-row { display: flex; flex-direction: column; gap: 6px; }
    .ticket-sheet__label { font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: #9ca3af; font-weight: 600; }
    .ticket-sheet__value { font-size: 18px; font-weight: 600; color: #111827; }
    .ticket-sheet__value.is-status { color: #16a34a; }
    .ticket-sheet__note { margin-top: 28px; font-size: 14px; color: #4b5563; line-height: 1.5; }
    .print-toolbar { max-width: 720px; margin: 18px auto; text-align: center; color: #4b5563; }
    .print-toolbar button { background: #111827; color: #fff; border: none; padding: 12px 24px; border-radius: 999px; font-size: 15px; cursor: pointer; }
    .print-toolbar p { margin-top: 12px; font-size: 13px; }
    @media print {
      body { background: #fff; padding: 0; }
      main { box-shadow: none; border-radius: 0; }
      .print-toolbar { display: none; }
    }
  </style>
</head>
<body>
  <main>
    ${heroImg}
    <header class="ticket-sheet__header">
      <p class="ticket-sheet__meta">${seq}</p>
      <h1 class="ticket-sheet__title">${eventName}</h1>
      <p class="ticket-sheet__meta">${dateText}${timeText ? " // " + timeText : ""}</p>
      ${location ? `<p class="ticket-sheet__meta">${location}</p>` : ""}
    </header>
    <section class="ticket-sheet__grid">
      <div class="ticket-sheet__qr-wrapper">
						<img class="ticket-sheet__qr" src="${qrObjectUrl}" alt="QR estático ${seq}" />
        <div class="ticket-sheet__qr-note">Este QR estático pod?s imprimirlo o guardarlo como PDF. Se invalida cuando se escanee o si transferís la entrada.</div>
      </div>
      <div class="ticket-sheet__info">
        <div class="ticket-sheet__info-row">
          <span class="ticket-sheet__label">Estado</span>
          <span class="ticket-sheet__value is-status">${statusLabel}</span>
        </div>
        <div class="ticket-sheet__info-row">
          <span class="ticket-sheet__label">Orden</span>
          <span class="ticket-sheet__value">${orderLabel}</span>
        </div>
        ${
					location
						? `<div class="ticket-sheet__info-row">
          <span class="ticket-sheet__label">Lugar</span>
          <span class="ticket-sheet__value">${location}</span>
        </div>`
						: ""
				}
      </div>
    </section>
    <p class="ticket-sheet__note">Guardá este QR en tu celular o imprimilo. Solo funcionará una vez.</p>
  </main>
  <div class="print-toolbar">
    <button type="button" onclick="window.print()">Imprimir o descargar</button>
    <p>Tip: podes elegir &ldquo;Guardar como PDF&rdquo; para descargar tu ticket.</p>
  </div>
  <script>
    window.addEventListener("load", function() {
      setTimeout(() => window.print(), 200);
    });
    window.addEventListener("afterprint", function() {
      window.close();
    });
  </script>

</body>
</html>`;
			popup.document.open();
			popup.document.write(html);
			popup.document.close();
			setTimeout(() => URL.revokeObjectURL(qrObjectUrl), 60000);
		} catch (error) {
			writePlaceholder(
				"No pudimos generar el QR estático. Cerrá esta ventana e intentá nuevamente.",
			);
			toast.error(
				"No pudimos generar el QR estático. Reintentá en unos segundos.",
			);
		}
	};

	const submitTransfer = async () => {
		if (!selectedTicket?.id) return;
		const email = toEmail.trim();
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			try {
				toast.error("Ingresá un email válido");
			} catch {}
			return;
		}
		try {
			setSubmitting(true);
			const res = await initiateTicketTransferApi(
				token,
				selectedTicket.id,
				email,
			);
			const data = res?.data || {};
			if (data?.mode === "immediate") {
				try {
					toast.success(
						"Transferencia realizada: el destinatario ya tenía cuenta.",
					);
				} catch {}
			} else if (data?.mode === "pending") {
				try {
					toast.success("Enviamos un email con el link de reclamo.");
				} catch {}
			} else {
				try {
					toast.success("Solicitud de transferencia enviada.");
				} catch {}
			}
			setShowTransfer(false);
			fetchDetail();
		} catch (error) {
			handleError(error);
		} finally {
			setSubmitting(false);
		}
	};
	const openInstagram = () => {
		try {
			const candidates = [
				detail?.instagram,
				detail?.instagramUrl,
				detail?.instagramLink,
				detail?.social?.instagram,
				appSettings?.instagram,
			];
			const url = candidates.find(
				(v) => typeof v === "string" && v.trim().length > 0,
			);
			if (url) {
				const href = url.startsWith("http")
					? url
					: `https://www.instagram.com/${url.replace(/^@/, "")}`;
				window.open(href, "_blank");
			} else {
				window.open("https://www.instagram.com/", "_blank");
			}
		} catch (_) {
			try {
				window.open("https://www.instagram.com/", "_blank");
			} catch {}
		}
	};

	return (
		<>
			<div className="myevent-detail">
				<h1 className="detail-page-title">Detalles de mi evento</h1>
				{/* Hero */}
				<div className="detail-hero">
					<div className="detail-hero-media">
						<img className="detail-hero-img" src={detail.image} alt="banner" />
						<button
							type="button"
							className="share-btn"
							onClick={handleShare}
							aria-label="Compartir"
						>
							<img src={shareIcon} alt="" />
						</button>
						<button
							type="button"
							className="ig-btn"
							onClick={openInstagram}
							aria-label="Instagram"
						>
							<img src={instagramIcon} alt="" />
						</button>
					</div>
					<div>
						<h2 className="detail-title">{detail.name}</h2>
						<div className="detail-meta">
							<div className="detail-meta-item">
								<img src={Calendar} alt="fecha" /> {dateLabel} // {timeLabel} hs
							</div>
							<div className="detail-meta-item">
								<img src={ubi} alt="ubicación" /> {detail.fulladdress}
							</div>
							<div className="detail-meta-item">
								<img src={ticketIco} alt="tickets" /> {ticketsCount}{" "}
								{ticketsCount === 1 ? "entrada comprada" : "entradas compradas"}
							</div>
						</div>
						<div className="detail-actions">
							<button
								className="detail-btn"
								onClick={() => navigate(`/event/${detail.eventid}`)}
							>
								Ver página del evento
							</button>
							<button
								className="detail-btn alt"
								onClick={() => {
									const url =
										detail.latitude && detail.longitude
											? `https://www.google.com/maps?q=${detail.latitude},${detail.longitude}`
											: `https://www.google.com/maps/search/${encodeURIComponent(detail.fulladdress || detail.name)}`;
									window.open(url, "_blank");
								}}
							>
								Ver mapa
							</button>
						</div>
					</div>
				</div>

				{/* Tickets */}
				{orderedTickets.length > 0 && (
					<section className="tickets-section">
						{/*<h3 className="tickets-title">Mis tickets</h3>*/}
						<div className="ticket-grid" ref={gridRef}>
							{orderedTickets.map((t) => {
								const [hh = "00", mm = "00"] = String(
									detail.timeFrom || "00:00",
								).split(":");
								const eventStart = dayjs(
									String(detail.startDate || "").slice(0, 10),
								)
									.hour(Number(hh))
									.minute(Number(mm));
								const hoursLeft = eventStart.diff(dayjs(), "hour", true);
								const isActive = dayjs().isAfter(eventStart); // evento ya comenzó
								const within12h = hoursLeft <= 12 && hoursLeft >= 0; // faltan 12hs o menos
								const within = within12h || isActive;
								const orderLabel = getOrganizerOrderLabel(detail, t) || "";
								const d = dayjs(String(detail.startDate || "").slice(0, 10));
								const datePretty = d.isValid()
									? d.format("D MMM").toUpperCase()
									: "";
								const timePretty = String(detail.timeFrom || "").slice(0, 5);
								const { resolvedStatus, isTransferred, isTransferPending } =
									resolveTicketTransfer(t, currentUserId);
								const isRedeemed =
									String(t?.status || "").toLowerCase() === "redeemed";
								const qrPolicy = t?.qrPolicy || {
									version: 1,
									mode: "dynamic",
									dynamicRequired: true,
									staticAllowed: false,
								};
								const showDynamicQr = qrPolicy.dynamicRequired && !isTransferred && !isRedeemed;
								const showPrintButton =
									qrPolicy.staticAllowed && !isTransferred && t.status !== "redeemed";
								const canTransfer =
									within &&
									!isTransferred &&
									!isTransferPending &&
									t.status !== "redeemed";
								const buttonLabel = isTransferred
									? "Entrada transferida"
									: isTransferPending
										? "Pendiente"
										: "Transferir entrada";
								const ticketStateLabel =
									t.status === "redeemed"
										? "Usada"
										: isTransferred
											? "Transferida"
											: isTransferPending
												? "Pendiente"
												: "Vigente";
								const ticketStateClass =
									t.status === "redeemed"
										? "is-used"
										: isTransferred
											? "is-used"
											: isTransferPending
												? "is-pending"
												: "is-active";
								const transferTargetEmail = String(
									t?.transferTargetEmail ||
										t?.pendingTransfer?.toEmail ||
										t?.toEmail ||
										t?.receiverEmail ||
										"",
								).trim();
								const canOpenTicket = isMobile;
								const ticketViewMeta = {
									within,
									transferStatus: resolvedStatus,
									isTransferred,
									isPending: isTransferPending,
									transferExpiresAt: t?.transferExpiresAt || null,
									orderLabel,
									datePretty,
									timePretty,
									transferTargetEmail,
								};
								const handleOpenTicket = () => openTicket(t, ticketViewMeta);
								return (
									<div key={t.id} className="ticket-item">
										<article
											className={`ticket-card ${canOpenTicket ? "ticket-card--clickable" : ""} ${isTransferred ? "is-transferred" : isTransferPending ? "is-transfer-pending" : ""}`}
											role={canOpenTicket ? "button" : undefined}
											tabIndex={canOpenTicket ? 0 : undefined}
											onClick={canOpenTicket ? handleOpenTicket : undefined}
											onKeyDown={
												canOpenTicket
													? (e) => {
															if (e.key === "Enter" || e.key === " ") {
																e.preventDefault();
																handleOpenTicket();
															}
														}
													: undefined
											}
											aria-label={
												canOpenTicket
													? `Ampliar QR de la entrada ${t.seq}`
													: undefined
											}
										>
											<div className="ticket-qr-box">
												{isTransferred ? (
													<div
														className="ticket-qr ticket-qr--placeholder"
														aria-hidden="true"
													>
														<span>Transferido</span>
													</div>
												) : isRedeemed ? (
													<div
														className="ticket-qr ticket-qr--placeholder"
														aria-hidden="true"
													>
														<span>Usada</span>
													</div>
												) : showDynamicQr ? (
									<RollingQr ticketId={t.id} accessToken={token} width={176} height={176} />
												) : (
									<AuthorizedQrImage
										className="ticket-qr"
										src={`${MAIN_API}/api/ticket/qr/ticket/${t.id}.png`}
										accessToken={token}
										alt={`Ticket ${t.seq}`}
													/>
												)}
											</div>
											<div className="ticket-divider" aria-hidden="true" />
											<div className="ticket-row">
												<div className="ticket-title">ENTRADA {t.seq}</div>
												<div className={`ticket-state ${ticketStateClass}`}>
													{ticketStateLabel}
												</div>
											</div>
											<dl
												className="ticket-meta"
												aria-label="Detalles de la entrada"
											>
												<div>
													<dt>Orden</dt>
													<dd>{orderLabel}</dd>
												</div>
												<div>
													<dt>Fecha</dt>
													<dd>{datePretty}</dd>
												</div>
												<div>
													<dt>Hora</dt>
													<dd>{timePretty}</dd>
												</div>
											</dl>
										</article>
										<div className="ticket-actions-out">
											<button
												className={`ticket-transfer ${canTransfer ? "is-active" : ""} ${isTransferPending ? "is-pending" : ""}`}
												disabled={!canTransfer}
												onClick={(e) => {
													e.stopPropagation();
													if (canTransfer) openTransfer(t);
												}}
												onKeyDown={(e) => e.stopPropagation()}
											>
												{buttonLabel}
											</button>
											{showPrintButton && (
												<button
													type="button"
													className="ticket-print"
													onClick={(e) => {
														e.stopPropagation();
														handlePrintTicket(t, {
															orderLabel,
															ticketStateLabel,
															datePretty,
															timePretty,
														});
													}}
													onKeyDown={(e) => e.stopPropagation()}
												>
													Imprimir entrada
												</button>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</section>
				)}

				{/* Rules / Help */}
				<section className="rules">
					<h3 className="rules-title">¿Cómo funcionan las entradas?</h3>
					<div className="rules-list">
						<div className="rules-item">
							<img src={qrIcon} alt="qr" />
													Las entradas configuradas para rotar muestran un QR dinámico en vivo.
													Las capturas del QR dinámico vencen y no sirven para ingresar. Solo
							el titular del ticket con su DNI en mano podrá ingresar al evento.
						</div>
						<div className="rules-item">
							<img src={transferIcon} alt="transferir" />
							La opción “Transferir entrada” se habilita 12 horas antes del
							evento. Podrás realizar una única transferencia por ticket. Una
							vez transferido, el nuevo titular será el único autorizado para
							ingresar con ese QR.
						</div>
						<div className="rules-item">
							<img src={calendario} alt="control" />
							En el control de acceso, se escanea el QR y se valida en tiempo
							real. Cada código puede ser usado solo una vez. Por seguridad, se
							recomienda no compartir capturas de pantalla ni reenviar el QR a
							terceros.
						</div>
					</div>
				</section>
			</div>
			<Modal
				show={showTicket}
				onHide={closeTicket}
				onExited={() => setTicketView(null)}
				centered
				className="ticket-view-modal"
				dialogClassName="ticket-view-dialog"
			>
				<Modal.Header closeButton>
					<Modal.Title>ENTRADA {activeTicket?.seq}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{activeTicket ? (
						<div className="ticket-view-content">
							<div className="ticket-view-qr">
								{metaTransferred ? (
									<div
										className="ticket-view-qr-placeholder"
										aria-hidden="true"
									>
										<span>Transferido</span>
									</div>
								) : isRedeemedTicket ? (
									<div
										className="ticket-view-qr-placeholder"
										aria-hidden="true"
									>
										<span>Usada</span>
									</div>
								) : isDynamicTicket ? (
									<RollingQr
										ticketId={activeTicket.id}
										accessToken={token}
										width={320}
										height={320}
									/>
								) : (
									<AuthorizedQrImage
										className="ticket-view-qr-img"
										src={staticQrUrl}
										accessToken={token}
										alt={`QR entrada ${activeTicket?.seq}`}
									/>
								)}
								<div className="ticket-view-note">
									{metaTransferred
										? "Entrada transferida. Solo el nuevo titular puede ingresar con este ticket."
										: isRedeemedTicket
											? "Esta entrada ya fue escaneada en el control de acceso."
										: metaPending
											? "Transferencia pendiente. Mientras no sea aceptada, seguis pudiendo usar este QR."
											: "Subi el brillo de tu pantalla y mostra este codigo al personal de acceso. Recorda tener tu DNI a mano para validar la entrada."}
								</div>
							</div>
							<div className="ticket-view-details">
								<div className="ticket-view-event">
									<h4>{detail.name}</h4>
									<p>
										{dateLabel} // {timeLabel} hs
									</p>
									<p>{detail.fulladdress}</p>
								</div>
								<div className="ticket-view-meta">
									<div>
										<span className="label">Estado</span>
										<span className={`value ${stateValueClass}`}>
											{ticketStateLabel}
										</span>
									</div>
									<div>
										<span className="label">Orden</span>
										<span className="value">
											{ticketMeta.orderLabel || "-"}
										</span>
									</div>
									{ticketMeta.transferTargetEmail ? (
										<div>
											<span className="label">
												{metaTransferred
													? "Transferida a"
													: metaPending
														? "Pendiente para"
														: "Destino"}
											</span>
											<span className={`value ${transferValueClass}`}>
												{ticketMeta.transferTargetEmail}
											</span>
										</div>
									) : null}
								</div>
							</div>
						</div>
					) : null}
				</Modal.Body>
			</Modal>

			<Modal
				show={showTransfer}
				onHide={() => setShowTransfer(false)}
				centered
				className="transfer-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>Transferir entrada</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						Vas a transferir la <strong>ENTRADA {selectedTicket?.seq}</strong>.
						Ingresá el email del destinatario:
					</p>
					<Form.Control
						type="email"
						placeholder="destinatario@dominio.com"
						value={toEmail}
						onChange={(e) => setToEmail(e.target.value)}
						disabled={submitting}
					/>
					<div className="order-date" style={{ marginTop: 15 }}>
						Si no tiene cuenta, recibirá un correo con el link de reclamo.
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="secondary"
						onClick={() => setShowTransfer(false)}
						disabled={submitting}
					>
						Cancelar
					</Button>
					<Button
						onClick={submitTransfer}
						disabled={submitting}
						variant="primary"
					>
						{submitting ? "Enviando..." : "Enviar"}
					</Button>
				</Modal.Footer>
			</Modal>
			<ToastContainer
				position="top-right"
				autoClose={3000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="light"
			/>
		</>
	);
};
export default MyEventDetail;
